import React from 'react';

import { T } from './login.theme';

/**
 * Stroke icons on a 24px grid, drawn inline so they inherit colour and never
 * pull a font or a network request into the sign-in path.
 */

const base = (size) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
});

export const PlusIcon = ({ size = 14, width = 2.4 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const CloseIcon = ({ size = 15, width = 2.2 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

export const CheckIcon = ({ size = 15, width = 2.8 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <path d="M4 12.5l5.5 5.5L20 7" />
  </svg>
);

export const LockIcon = ({ size = 16, width = 1.8 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <rect x="4" y="10.5" width="16" height="10" rx="2.5" />
    <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
  </svg>
);

export const EyeIcon = ({ size = 17, width = 1.8 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export const EyeOffIcon = ({ size = 17, width = 1.8 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <path d="M2 12s3.6-7 10-7c2 0 3.7.7 5.1 1.6M22 12s-3.6 7-10 7c-2 0-3.7-.7-5.1-1.6" />
    <path d="M4 4l16 16" />
  </svg>
);

export const AlertIcon = ({ size = 15, width = 2 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5v5.5M12 16.4v.01" />
  </svg>
);

export const InfoIcon = ({ size = 14, width = 1.9 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5.5M12 7.8v.01" />
  </svg>
);

export const ClockIcon = ({ size = 14, width = 2 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5V12l3 2" />
  </svg>
);

export const PrinterIcon = ({ size = 16, width = 1.8 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <path d="M7 9V3.5h10V9" />
    <rect x="3.5" y="9" width="17" height="7.5" rx="2" />
    <path d="M7 14.5h10V21H7z" />
  </svg>
);

export const PeopleIcon = ({ size = 17, width = 1.8 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <circle cx="12" cy="8.5" r="3.8" />
    <path d="M4.8 20c0-3.4 3.2-5.6 7.2-5.6s7.2 2.2 7.2 5.6" />
  </svg>
);

/**
 * A speech bubble with a tail, for the support links.
 *
 * Deliberately a plain bubble rather than WhatsApp's own mark: it sits right
 * next to the number so it reads correctly either way, it inherits colour like
 * every other icon here, and the app stays clear of someone else's logo.
 */
export const ChatIcon = ({ size = 13, width = 2 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <path d="M21 11.5a8.5 8.5 0 0 1-12.6 7.4L3.5 20.5l1.7-4.8A8.5 8.5 0 1 1 21 11.5z" />
  </svg>
);

export const BackspaceIcon = ({ size = 26, width = 1.9 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <path d="M9 5h10.5A1.5 1.5 0 0 1 21 6.5v11a1.5 1.5 0 0 1-1.5 1.5H9L2.5 12z" />
    <path d="M12 9.5l5 5M17 9.5l-5 5" />
  </svg>
);

/** A ring that spins — the one animation on this screen. */
export const Spinner = ({ size = 16, width = 2.6 }) => (
  <svg
    {...base(size)}
    strokeWidth={width}
    style={{ animation: 'dalabtireSpin 0.8s linear infinite' }}
  >
    <path d="M12 3.5a8.5 8.5 0 1 1-8.5 8.5" opacity="0.4" />
    <path d="M12 3.5a8.5 8.5 0 0 1 8.5 8.5" />
  </svg>
);

/**
 * Every keyframe this screen uses, rendered once by it rather than added to
 * App.css, so the feature owns everything it needs.
 *
 * `dalabtireShake` is the wrong-PIN nudge: short, small, and horizontal — it
 * has to read as "that was wrong, go again" from arm's length at a till, not
 * as the screen breaking.
 *
 * The two `.dalabtire-support-link` rules are here for the one thing inline
 * styles cannot express: hover and keyboard focus. The support numbers are
 * buttons rather than anchors — the till hands its links to the operating
 * system rather than following them — so they need the affordance an anchor
 * would have had for free.
 */
export const LoginKeyframes = () => (
  <style>
    {'@keyframes dalabtireSpin { to { transform: rotate(360deg); } }' +
      '@keyframes dalabtireShake {' +
      '10%, 90% { transform: translateX(-2px); }' +
      '20%, 80% { transform: translateX(4px); }' +
      '30%, 50%, 70% { transform: translateX(-7px); }' +
      '40%, 60% { transform: translateX(7px); }' +
      '100% { transform: translateX(0); } }' +
      '.dalabtire-support-link:hover { text-decoration: underline; }' +
      '.dalabtire-support-link:focus-visible { outline: 2px solid ' +
      T.whatsapp +
      '; outline-offset: 3px; border-radius: 4px; }'}
  </style>
);
